import { useEffect, useState, useCallback, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Download, FileText, Plus, Trash2, MessageSquare, Printer, FileDown } from "lucide-react";
import { api, formatUSD, fullUSD } from "../lib/api";
import { useAuth } from "../lib/auth";
import { TEST_IDS } from "../constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const CHART_COLORS = ["#0B1F3A", "#3399FF", "#D4AF37", "#5F6B7A", "#8CA5C4", "#B4794A", "#3D7BC7"];
const STAGES = ["Sourcing", "Diligence", "IC Review", "Negotiation", "Closing", "Closed", "Passed"];
const SECTORS = ["Energy", "Infrastructure", "Commodities", "Technology", "Real Assets"];
const REGIONS = ["North America", "Asia", "Africa", "Europe", "Latin America", "Middle East"];
const PROJECT_STATUS = ["Planning", "Active", "On Hold", "Completed", "Cancelled"];

export default function Dashboard() {
  const { user } = useAuth();
  const [window_, setWindow_] = useState("3m");
  const [overview, setOverview] = useState(null);
  const [deals, setDeals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, d, p, n] = await Promise.all([
        api.get(`/dashboard/overview?window=${window_}`),
        api.get("/deals"),
        api.get("/projects"),
        api.get("/notes"),
      ]);
      setOverview(o.data);
      setDeals(d.data);
      setProjects(p.data);
      setNotes(n.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [window_]);

  useEffect(() => { load(); }, [load]);

  const seedDemo = async () => {
    try {
      const { data } = await api.post("/seed/demo");
      toast.success(`Seeded ${data.deals_added} deals, ${data.projects_added} projects`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const exportPDF = async () => {
    // Use browser print — cleanest institutional look; user can Save as PDF
    window.print();
  };

  const exportPPTX = async () => {
    try {
      // Load pptxgenjs from CDN on demand to avoid webpack node:fs resolution
      if (!window.PptxGenJS) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/gh/gitbrent/PptxGenJS@3.12.0/dist/pptxgen.bundle.js";
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      const PptxGenJS = window.PptxGenJS;
      const pptx = new PptxGenJS();
      pptx.layout = "LAYOUT_WIDE";
      pptx.defineSlideMaster({
        title: "FPC",
        background: { color: "F8F7F2" },
        objects: [
          { rect: { x: 0, y: 0, w: "100%", h: 0.35, fill: { color: "0B1F3A" } } },
          { rect: { x: 0, y: 0.35, w: "100%", h: 0.02, fill: { color: "D4AF37" } } },
          { text: { text: "First Providence Capital · Confidential", options: { x: 0.5, y: 0.06, fontSize: 10, color: "F8F7F2", fontFace: "Calibri" } } },
        ],
      });

      // Slide 1: Title
      let s = pptx.addSlide({ masterName: "FPC" });
      s.addText("Portfolio & Pipeline Briefing", { x: 0.5, y: 1.2, w: 12, h: 1, fontSize: 44, color: "0B1F3A", fontFace: "Calibri Light" });
      s.addText(`Window: ${window_.toUpperCase()} · ${new Date().toLocaleDateString()}`, { x: 0.5, y: 2.2, fontSize: 14, color: "5F6B7A" });

      const k = overview?.kpis || {};
      // Slide 2: KPIs
      s = pptx.addSlide({ masterName: "FPC" });
      s.addText("Key Metrics", { x: 0.5, y: 0.6, fontSize: 24, color: "0B1F3A", bold: true });
      const kpis = [
        ["Total Pipeline", fullUSD(k.total_pipeline)],
        ["Weighted Pipeline", fullUSD(k.weighted_pipeline)],
        ["Forecast Revenue", fullUSD(k.forecast_revenue)],
        ["Active Projects", `${k.active_projects || 0} / ${k.total_projects || 0}`],
        ["Capital Deployed", fullUSD(k.capital_deployed)],
        ["Total Deals", String(k.total_deals || 0)],
      ];
      kpis.forEach(([label, value], i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = 0.5 + col * 4.3;
        const y = 1.4 + row * 1.5;
        s.addShape(pptx.ShapeType.rect, { x, y, w: 4, h: 1.3, fill: { color: "FFFFFF" }, line: { color: "0B1F3A", width: 0.5 } });
        s.addText(label, { x: x + 0.2, y: y + 0.15, fontSize: 10, color: "5F6B7A", bold: true });
        s.addText(value, { x: x + 0.2, y: y + 0.45, fontSize: 22, color: "0B1F3A", bold: true });
      });

      // Slide 3: Deals table
      s = pptx.addSlide({ masterName: "FPC" });
      s.addText("Deals Pipeline", { x: 0.5, y: 0.6, fontSize: 24, color: "0B1F3A", bold: true });
      const rows = [[
        { text: "Deal", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
        { text: "Sector", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
        { text: "Region", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
        { text: "Stage", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
        { text: "Size", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
        { text: "Prob.", options: { bold: true, color: "F8F7F2", fill: "0B1F3A" } },
      ]];
      deals.slice(0, 12).forEach(d => rows.push([
        d.name, d.sector, d.region, d.stage,
        fullUSD(d.deal_size_usd),
        `${d.probability}%`,
      ]));
      s.addTable(rows, { x: 0.5, y: 1.2, w: 12, colW: [3.5, 1.8, 1.8, 1.8, 1.6, 1.5], fontSize: 10, fontFace: "Calibri" });

      // Slide 4: Sectors chart
      s = pptx.addSlide({ masterName: "FPC" });
      s.addText("Sector Allocation", { x: 0.5, y: 0.6, fontSize: 24, color: "0B1F3A", bold: true });
      const sectorData = (overview?.sectors || []).filter(x => x.value > 0);
      if (sectorData.length) {
        s.addChart(pptx.ChartType.doughnut, [{
          name: "Sectors",
          labels: sectorData.map(x => x.sector),
          values: sectorData.map(x => Math.round(x.value)),
        }], { x: 0.5, y: 1, w: 12, h: 5.5, showLegend: true, legendPos: "r" });
      }

      await pptx.writeFile({ fileName: `FPC_Dashboard_${window_}.pptx` });
      toast.success("PPTX exported");
    } catch (e) {
      toast.error("PPTX export failed");
      console.error(e);
    }
  };

  if (!user) return null;
  const k = overview?.kpis || {};
  const stageData = (overview?.stages || []).map(s => ({ ...s, value: s.value / 1_000_000 }));
  const sectorData = (overview?.sectors || []).map(s => ({ ...s, value: s.value / 1_000_000 }));
  const regionData = (overview?.regions || []).map(s => ({ ...s, value: s.value / 1_000_000 }));
  const monthly = overview?.monthly || [];

  return (
    <div className="min-h-screen bg-[#F8F7F2]" ref={printRef}>
      {/* Header */}
      <div className="border-b border-[#0B1F3A]/10 bg-white sticky top-20 z-10 no-print">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="eyebrow">Admin Dashboard</div>
            <h1 className="font-display text-3xl mt-1 text-[#0B1F3A]">Deals & Projects Command</h1>
          </div>
          <div className="flex items-center gap-3">
            <Select value={window_} onValueChange={setWindow_}>
              <SelectTrigger data-testid={TEST_IDS.dash.windowSelect} className="w-[140px] rounded-none border-[#0B1F3A]/20 text-xs uppercase tracking-widest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1m">Last 30 days</SelectItem>
                <SelectItem value="3m">Last 3 months</SelectItem>
                <SelectItem value="6m">Last 6 months</SelectItem>
                <SelectItem value="1y">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid={TEST_IDS.dash.exportPdf}
              onClick={exportPDF}
              variant="outline"
              className="rounded-none border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-[#F8F7F2] text-xs uppercase tracking-widest gap-2"
            >
              <Printer size={14} /> PDF
            </Button>
            <Button
              data-testid={TEST_IDS.dash.exportPptx}
              onClick={exportPPTX}
              className="rounded-none bg-[#D4AF37] text-[#0B1F3A] hover:bg-[#c19e2f] text-xs uppercase tracking-widest gap-2"
            >
              <Download size={14} /> PPTX
            </Button>
            {user?.role === "admin" && deals.length === 0 && (
              <Button data-testid={TEST_IDS.dash.seedDemo} onClick={seedDemo} className="rounded-none bg-[#3399FF] text-white hover:bg-[#164a8c] text-xs uppercase tracking-widest">
                Seed Demo
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8">
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 print-page">
          <KpiCard label="Total Pipeline" value={formatUSD(k.total_pipeline)} sub={`${k.total_deals || 0} deals`} />
          <KpiCard label="Weighted Pipeline" value={formatUSD(k.weighted_pipeline)} sub="Prob-weighted" />
          <KpiCard label="Forecast Revenue" value={formatUSD(k.forecast_revenue)} sub={`Window ${window_.toUpperCase()}`} />
          <KpiCard label="Active Projects" value={String(k.active_projects || 0)} sub={`${formatUSD(k.capital_deployed)} deployed`} />
        </div>

        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="rounded-none bg-white border border-[#0B1F3A]/10 h-12 p-0 no-print">
            <TabsTrigger data-testid={TEST_IDS.dash.tabAnalytics} value="analytics" className="rounded-none px-6 h-full data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-[#F8F7F2] text-xs uppercase tracking-widest">Analytics</TabsTrigger>
            <TabsTrigger data-testid={TEST_IDS.dash.tabDeals} value="deals" className="rounded-none px-6 h-full data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-[#F8F7F2] text-xs uppercase tracking-widest">Deals Pipeline</TabsTrigger>
            <TabsTrigger data-testid={TEST_IDS.dash.tabProjects} value="projects" className="rounded-none px-6 h-full data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-[#F8F7F2] text-xs uppercase tracking-widest">Projects</TabsTrigger>
            <TabsTrigger data-testid={TEST_IDS.dash.tabNotes} value="notes" className="rounded-none px-6 h-full data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-[#F8F7F2] text-xs uppercase tracking-widest">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6 space-y-6 print-page">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Pipeline by Stage" subtitle="Deal value in $M">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,31,58,0.08)" />
                    <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#5F6B7A" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#5F6B7A" }} />
                    <Tooltip contentStyle={{ background: "#0B1F3A", border: "none", color: "#F8F7F2" }} formatter={(v) => `$${v.toFixed(1)}M`} />
                    <Bar dataKey="value" fill="#3399FF" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Sector Allocation" subtitle="Deals + capital deployed">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={sectorData} dataKey="value" nameKey="sector" outerRadius={100} innerRadius={55}>
                      {sectorData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `$${v.toFixed(1)}M`} contentStyle={{ background: "#0B1F3A", border: "none", color: "#F8F7F2" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Geographic Allocation" subtitle="By region ($M)">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={regionData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,31,58,0.08)" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#5F6B7A" }} />
                    <YAxis dataKey="region" type="category" tick={{ fontSize: 11, fill: "#5F6B7A" }} width={110} />
                    <Tooltip formatter={(v) => `$${v.toFixed(1)}M`} contentStyle={{ background: "#0B1F3A", border: "none", color: "#F8F7F2" }} />
                    <Bar dataKey="value" fill="#D4AF37" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Forecast Revenue by Close Month" subtitle="Weighted vs. gross forecast">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={monthly.map(m => ({ ...m, forecast: m.forecast / 1_000_000, weighted: m.weighted / 1_000_000 }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,31,58,0.08)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#5F6B7A" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#5F6B7A" }} />
                    <Tooltip formatter={(v) => `$${v.toFixed(2)}M`} contentStyle={{ background: "#0B1F3A", border: "none", color: "#F8F7F2" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="forecast" stroke="#0B1F3A" strokeWidth={2} name="Gross" />
                    <Line type="monotone" dataKey="weighted" stroke="#D4AF37" strokeWidth={2} name="Weighted" />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          <TabsContent value="deals" className="mt-6">
            <DealsTab deals={deals} onChanged={load} />
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <ProjectsTab projects={projects} deals={deals} onChanged={load} />
          </TabsContent>

          <TabsContent value="notes" className="mt-6">
            <NotesTab notes={notes} deals={deals} projects={projects} onChanged={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#0B1F3A]/10 p-6 border-l-4 border-l-[#D4AF37]"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#5F6B7A]">{label}</div>
      <div className="numeral text-4xl text-[#0B1F3A] mt-2">{value}</div>
      <div className="text-xs text-[#5F6B7A] mt-1">{sub}</div>
    </motion.div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#0B1F3A]/10 p-6">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-display text-xl text-[#0B1F3A]">{title}</div>
          <div className="text-xs text-[#5F6B7A] mt-0.5">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ---------------- Deals Tab ----------------
function DealsTab({ deals, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDeal());
  const [editingId, setEditingId] = useState(null);
  const { user } = useAuth();

  function emptyDeal() {
    return { name: "", sector: "Energy", region: "North America", stage: "Sourcing",
      deal_size_usd: 0, forecast_revenue_usd: 0, probability: 50, close_date: "",
      lead_partner: "", description: "" };
  }

  const openNew = () => { setForm(emptyDeal()); setEditingId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...d, close_date: d.close_date || "" }); setEditingId(d.deal_id); setOpen(true); };

  const submit = async () => {
    const payload = {
      ...form,
      deal_size_usd: Number(form.deal_size_usd),
      forecast_revenue_usd: Number(form.forecast_revenue_usd),
      probability: Number(form.probability),
      close_date: form.close_date || null,
    };
    try {
      if (editingId) await api.put(`/deals/${editingId}`, payload);
      else await api.post("/deals", payload);
      toast.success(editingId ? "Deal updated" : "Deal created");
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this deal?")) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="bg-white border border-[#0B1F3A]/10 print-page">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B1F3A]/10 no-print">
        <div>
          <div className="font-display text-xl text-[#0B1F3A]">Deals Pipeline</div>
          <div className="text-xs text-[#5F6B7A]">{deals.length} active items</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TEST_IDS.dash.newDeal} onClick={openNew} className="rounded-none bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF] gap-2 text-xs uppercase tracking-widest">
              <Plus size={14} /> New Deal
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit Deal" : "New Deal"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormLabel label="Deal Name" className="col-span-2"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Sector">
                <Select value={form.sector} onValueChange={v => setForm({ ...form, sector: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Region">
                <Select value={form.region} onValueChange={v => setForm({ ...form, region: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Stage">
                <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Deal Size (USD)"><Input type="number" value={form.deal_size_usd} onChange={e => setForm({ ...form, deal_size_usd: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Forecast Revenue (USD)"><Input type="number" value={form.forecast_revenue_usd} onChange={e => setForm({ ...form, forecast_revenue_usd: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Probability %"><Input type="number" min={0} max={100} value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Expected Close"><Input type="date" value={form.close_date?.slice(0, 10) || ""} onChange={e => setForm({ ...form, close_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-none" /></FormLabel>
              <FormLabel label="Lead Partner"><Input value={form.lead_partner} onChange={e => setForm({ ...form, lead_partner: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Description" className="col-span-2"><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none" /></FormLabel>
            </div>
            <DialogFooter>
              <Button onClick={submit} className="rounded-none bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF]">{editingId ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F7F2] text-[10px] uppercase tracking-widest text-[#5F6B7A]">
            <tr>
              <th className="text-left px-6 py-3">Deal</th>
              <th className="text-left px-4 py-3">Sector</th>
              <th className="text-left px-4 py-3">Region</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-right px-4 py-3">Size</th>
              <th className="text-right px-4 py-3">Forecast</th>
              <th className="text-right px-4 py-3">Prob</th>
              <th className="text-left px-4 py-3">Lead</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#0B1F3A]/10">
            {deals.length === 0 && (
              <tr><td colSpan={9} className="text-center py-16 text-[#5F6B7A]">No deals yet. Click "New Deal" or "Seed Demo" to get started.</td></tr>
            )}
            {deals.map(d => (
              <tr key={d.deal_id} data-testid={`${TEST_IDS.dash.dealRow}-${d.deal_id}`} className="hover:bg-[#F8F7F2] cursor-pointer" onClick={() => openEdit(d)}>
                <td className="px-6 py-4 text-[#0B1F3A] font-medium">{d.name}</td>
                <td className="px-4 py-4 text-[#5F6B7A]">{d.sector}</td>
                <td className="px-4 py-4 text-[#5F6B7A]">{d.region}</td>
                <td className="px-4 py-4"><Badge className="rounded-none bg-[#3399FF]/10 text-[#3399FF] hover:bg-[#3399FF]/10">{d.stage}</Badge></td>
                <td className="px-4 py-4 text-right numeral text-lg text-[#0B1F3A]">{formatUSD(d.deal_size_usd)}</td>
                <td className="px-4 py-4 text-right text-[#5F6B7A]">{formatUSD(d.forecast_revenue_usd)}</td>
                <td className="px-4 py-4 text-right"><span className="text-[#D4AF37] font-semibold">{d.probability}%</span></td>
                <td className="px-4 py-4 text-[#5F6B7A] text-xs">{d.lead_partner}</td>
                <td className="px-4 py-4 no-print">
                  {user?.role === "admin" && (
                    <button onClick={(e) => { e.stopPropagation(); del(d.deal_id); }} className="text-[#5F6B7A] hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormLabel({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#5F6B7A] mb-1">{label}</div>
      {children}
    </label>
  );
}

// ---------------- Projects Tab ----------------
function ProjectsTab({ projects, deals, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [briefProject, setBriefProject] = useState(null);
  const { user } = useAuth();

  function empty() {
    return { name: "", sector: "Energy", region: "North America", status: "Planning",
      capital_deployed_usd: 0, projected_revenue_usd: 0, progress_pct: 0,
      start_date: "", target_end_date: "", lead: "", description: "", deal_id: "",
      // Extended offering fields
      strategic_goal: "", brief_overview: "",
      deal_size_usd: 0,
      financing_structure: "", profit_model: "", cost_analysis: "", pro_forma: "",
      fund_management_fee_pct: 2, fund_participants_share_pct: 20, fund_participants: "" };
  }

  const openNew = () => { setForm(empty()); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, start_date: p.start_date || "", target_end_date: p.target_end_date || "" }); setEditingId(p.project_id); setOpen(true); };

  const submit = async () => {
    const payload = {
      ...form,
      capital_deployed_usd: Number(form.capital_deployed_usd) || 0,
      projected_revenue_usd: Number(form.projected_revenue_usd) || 0,
      progress_pct: Number(form.progress_pct) || 0,
      deal_size_usd: form.deal_size_usd === "" || form.deal_size_usd === null ? null : Number(form.deal_size_usd),
      fund_management_fee_pct: form.fund_management_fee_pct === "" || form.fund_management_fee_pct === null ? null : Number(form.fund_management_fee_pct),
      fund_participants_share_pct: form.fund_participants_share_pct === "" || form.fund_participants_share_pct === null ? null : Number(form.fund_participants_share_pct),
      start_date: form.start_date || null,
      target_end_date: form.target_end_date || null,
      deal_id: form.deal_id || null,
    };
    try {
      if (editingId) await api.put(`/projects/${editingId}`, payload);
      else await api.post("/projects", payload);
      toast.success(editingId ? "Project updated" : "Project created");
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="bg-white border border-[#0B1F3A]/10 print-page">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B1F3A]/10 no-print">
        <div>
          <div className="font-display text-xl text-[#0B1F3A]">Projects</div>
          <div className="text-xs text-[#5F6B7A]">{projects.length} projects tracked</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TEST_IDS.dash.newProject} onClick={openNew} className="rounded-none bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF] gap-2 text-xs uppercase tracking-widest">
              <Plus size={14} /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto thin-scroll">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>

            <SectionHeader label="Basics" />
            <div className="grid grid-cols-2 gap-4">
              <FormLabel label="Project Name" className="col-span-2"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Strategic Goal (one sentence)" className="col-span-2">
                <Input value={form.strategic_goal || ""} onChange={e => setForm({ ...form, strategic_goal: e.target.value })} className="rounded-none" placeholder="e.g. To expand 5G infrastructure across underserved rural markets." />
              </FormLabel>
              <FormLabel label="Sector">
                <Select value={form.sector} onValueChange={v => setForm({ ...form, sector: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Region">
                <Select value={form.region} onValueChange={v => setForm({ ...form, region: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Status">
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Linked Deal (optional)">
                <Select value={form.deal_id || "none"} onValueChange={v => setForm({ ...form, deal_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {deals.map(d => <SelectItem key={d.deal_id} value={d.deal_id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label="Lead"><Input value={form.lead} onChange={e => setForm({ ...form, lead: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Date Started"><Input type="date" value={form.start_date?.slice(0, 10) || ""} onChange={e => setForm({ ...form, start_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-none" /></FormLabel>
              <FormLabel label="Date to End"><Input type="date" value={form.target_end_date?.slice(0, 10) || ""} onChange={e => setForm({ ...form, target_end_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="rounded-none" /></FormLabel>
              <FormLabel label="Progress %"><Input type="number" min={0} max={100} value={form.progress_pct} onChange={e => setForm({ ...form, progress_pct: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Brief Overview (≤ 1 paragraph)" className="col-span-2">
                <Textarea rows={4} maxLength={800} value={form.brief_overview || ""} onChange={e => setForm({ ...form, brief_overview: e.target.value })} className="rounded-none" placeholder="Executive summary — no more than one paragraph." />
                <div className="text-[10px] uppercase tracking-widest text-[#5F6B7A] mt-1">{(form.brief_overview || "").length}/800</div>
              </FormLabel>
              <FormLabel label="Internal Description" className="col-span-2"><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none" placeholder="Optional internal notes (not printed on the brief)." /></FormLabel>
            </div>

            <SectionHeader label="Deal Economics" />
            <div className="grid grid-cols-2 gap-4">
              <FormLabel label="Size of the Deal (USD)"><Input type="number" value={form.deal_size_usd ?? ""} onChange={e => setForm({ ...form, deal_size_usd: e.target.value })} className="rounded-none" placeholder="Total raise / project cost" /></FormLabel>
              <FormLabel label="Capital Deployed to Date (USD)"><Input type="number" value={form.capital_deployed_usd} onChange={e => setForm({ ...form, capital_deployed_usd: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Projected Revenue (USD)" className="col-span-2"><Input type="number" value={form.projected_revenue_usd} onChange={e => setForm({ ...form, projected_revenue_usd: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Financing Structure" className="col-span-2">
                <Textarea rows={3} value={form.financing_structure || ""} onChange={e => setForm({ ...form, financing_structure: e.target.value })} className="rounded-none" placeholder="e.g. Senior debt 60% · Subordinated debt 20% · Fund equity 20%." />
              </FormLabel>
              <FormLabel label="Profit Model Framework" className="col-span-2">
                <Textarea rows={3} value={form.profit_model || ""} onChange={e => setForm({ ...form, profit_model: e.target.value })} className="rounded-none" placeholder="Revenue streams and monetization mechanics." />
              </FormLabel>
              <FormLabel label="Cost Analysis" className="col-span-2">
                <Textarea rows={3} value={form.cost_analysis || ""} onChange={e => setForm({ ...form, cost_analysis: e.target.value })} className="rounded-none" placeholder="Capex, opex, and unit economics." />
              </FormLabel>
              <FormLabel label="Pro Forma Financial" className="col-span-2">
                <Textarea rows={4} value={form.pro_forma || ""} onChange={e => setForm({ ...form, pro_forma: e.target.value })} className="rounded-none" placeholder="Year-by-year projections, ROI, EBITDA, exit assumptions." />
              </FormLabel>
            </div>

            <SectionHeader label="Fund Terms" />
            <div className="grid grid-cols-2 gap-4">
              <FormLabel label="Fund Management Fee (%)"><Input type="number" step="0.1" value={form.fund_management_fee_pct ?? ""} onChange={e => setForm({ ...form, fund_management_fee_pct: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Fund Participants Share (%)"><Input type="number" step="0.1" value={form.fund_participants_share_pct ?? ""} onChange={e => setForm({ ...form, fund_participants_share_pct: e.target.value })} className="rounded-none" /></FormLabel>
              <FormLabel label="Fund Participants" className="col-span-2">
                <Textarea rows={3} value={form.fund_participants || ""} onChange={e => setForm({ ...form, fund_participants: e.target.value })} className="rounded-none" placeholder="Name participating investors, LPs, and co-investors." />
              </FormLabel>
            </div>

            <DialogFooter className="mt-6">
              <Button onClick={submit} className="rounded-none bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF]">{editingId ? "Save Project" : "Create Project"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="divide-y divide-[#0B1F3A]/10">
        {projects.length === 0 && <div className="text-center py-16 text-[#5F6B7A]">No projects yet.</div>}
        {projects.map(p => (
          <div key={p.project_id} data-testid={`${TEST_IDS.dash.projectRow}-${p.project_id}`} className="px-6 py-5 hover:bg-[#F8F7F2] cursor-pointer" onClick={() => openEdit(p)}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-3">
                  <div className="font-display text-lg text-[#0B1F3A]">{p.name}</div>
                  <Badge className="rounded-none bg-[#0B1F3A]/5 text-[#0B1F3A] hover:bg-[#0B1F3A]/5 text-[10px] uppercase tracking-widest">{p.status}</Badge>
                </div>
                <div className="text-xs text-[#5F6B7A] mt-1">{p.sector} · {p.region} · Lead: {p.lead || "—"}</div>
                {p.description && <div className="text-sm text-[#5F6B7A] mt-2 max-w-3xl">{p.description}</div>}
              </div>
              <div className="grid grid-cols-3 gap-6 min-w-[420px]">
                <Metric label="Deployed" value={formatUSD(p.capital_deployed_usd)} />
                <Metric label="Projected" value={formatUSD(p.projected_revenue_usd)} />
                <Metric label="Progress" value={`${p.progress_pct}%`} />
              </div>
              <div className="flex items-center gap-2 no-print">
                <button
                  onClick={(e) => { e.stopPropagation(); setBriefProject(p); }}
                  data-testid={`project-brief-${p.project_id}`}
                  className="inline-flex items-center gap-1.5 border border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-[#F8F7F2] px-3 py-1.5 text-[10px] uppercase tracking-widest transition"
                  title="Open project brief"
                >
                  <FileDown size={12} /> Brief
                </button>
                {user?.role === "admin" && (
                  <button onClick={(e) => { e.stopPropagation(); del(p.project_id); }} className="text-[#5F6B7A] hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 h-1.5 bg-[#0B1F3A]/10">
              <div className="h-full bg-[#D4AF37]" style={{ width: `${p.progress_pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      <ProjectBrief project={briefProject} onClose={() => setBriefProject(null)} />
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div className="mt-6 mb-3 flex items-center gap-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#3399FF] font-semibold whitespace-nowrap">{label}</div>
      <div className="flex-1 h-px bg-[#0B1F3A]/15" />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-[#5F6B7A]">{label}</div>
      <div className="numeral text-xl text-[#0B1F3A] mt-1">{value}</div>
    </div>
  );
}

// ---------------- Notes Tab ----------------
function NotesTab({ notes, deals, projects, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entity_type: "project", entity_id: "", body: "", visibility: "team", source: "portal" });
  const [waOpen, setWaOpen] = useState(false);
  const [waForm, setWaForm] = useState({ project_id: "", body: "", sender: "Lanre" });

  const submit = async () => {
    if (!form.entity_id || !form.body) return toast.error("Select an item and add note text");
    try {
      await api.post("/notes", form);
      toast.success("Note added");
      setForm({ ...form, body: "" });
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const submitWA = async () => {
    if (!waForm.project_id || !waForm.body) return toast.error("Select project & message");
    try {
      await api.post("/whatsapp/simulate", waForm);
      toast.success("WhatsApp update ingested");
      setWaForm({ ...waForm, body: "" });
      setWaOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const entities = form.entity_type === "deal" ? deals : projects;
  const entityLabel = (n) => {
    if (n.entity_type === "deal") return deals.find(d => d.deal_id === n.entity_id)?.name || n.entity_id;
    return projects.find(p => p.project_id === n.entity_id)?.name || n.entity_id;
  };

  return (
    <div className="grid md:grid-cols-3 gap-6 print-page">
      <div className="md:col-span-2 bg-white border border-[#0B1F3A]/10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#0B1F3A]/10 no-print">
          <div>
            <div className="font-display text-xl text-[#0B1F3A]">Notes & Updates</div>
            <div className="text-xs text-[#5F6B7A]">{notes.length} total</div>
          </div>
          <div className="flex gap-2">
            <Dialog open={waOpen} onOpenChange={setWaOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none border-[#25D366] text-[#128C7E] hover:bg-[#128C7E] hover:text-white gap-2 text-xs uppercase tracking-widest">
                  <MessageSquare size={14} /> WhatsApp Update
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl">Log WhatsApp Update</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="text-xs text-[#5F6B7A]">Simulates an inbound WhatsApp update from Lanre — routes into the project timeline.</div>
                  <FormLabel label="Project">
                    <Select value={waForm.project_id} onValueChange={v => setWaForm({ ...waForm, project_id: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </FormLabel>
                  <FormLabel label="Sender"><Input value={waForm.sender} onChange={e => setWaForm({ ...waForm, sender: e.target.value })} className="rounded-none" /></FormLabel>
                  <FormLabel label="Update"><Textarea rows={4} value={waForm.body} onChange={e => setWaForm({ ...waForm, body: e.target.value })} className="rounded-none" placeholder="e.g. Cargo cleared Qingdao customs, discharge starts Thursday…" /></FormLabel>
                </div>
                <DialogFooter><Button onClick={submitWA} className="rounded-none bg-[#128C7E] hover:bg-[#0E6E63] text-white">Log Update</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid={TEST_IDS.dash.newNote} className="rounded-none bg-[#0B1F3A] text-[#F8F7F2] hover:bg-[#3399FF] gap-2 text-xs uppercase tracking-widest">
                  <Plus size={14} /> Note
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg">
                <DialogHeader><DialogTitle className="font-display text-2xl">Add Note</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <FormLabel label="Attach To">
                    <Select value={form.entity_type} onValueChange={v => setForm({ ...form, entity_type: v, entity_id: "" })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deal">Deal</SelectItem>
                        <SelectItem value="project">Project</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormLabel>
                  <FormLabel label="Item">
                    <Select value={form.entity_id} onValueChange={v => setForm({ ...form, entity_id: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>
                        {entities.map(x => <SelectItem key={x.deal_id || x.project_id} value={x.deal_id || x.project_id}>{x.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormLabel>
                  <FormLabel label="Visibility">
                    <Select value={form.visibility} onValueChange={v => setForm({ ...form, visibility: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Private (only me)</SelectItem>
                        <SelectItem value="team">Team</SelectItem>
                        <SelectItem value="shared">Shared with counterparty</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormLabel>
                  <FormLabel label="Note"><Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className="rounded-none" /></FormLabel>
                </div>
                <DialogFooter><Button onClick={submit} className="rounded-none bg-[#0B1F3A] hover:bg-[#3399FF] text-white">Save Note</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="divide-y divide-[#0B1F3A]/10 max-h-[720px] overflow-y-auto thin-scroll">
          {notes.length === 0 && <div className="text-center py-16 text-[#5F6B7A]">No notes yet.</div>}
          {notes.map(n => (
            <div key={n.note_id} className="px-6 py-5">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#5F6B7A]">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 ${n.source === "whatsapp" ? "bg-[#128C7E]/10 text-[#128C7E]" : "bg-[#3399FF]/10 text-[#3399FF]"}`}>
                    {n.source}
                  </span>
                  <span className="text-[#0B1F3A]">{n.entity_type}</span>
                  <span>·</span>
                  <span>{entityLabel(n)}</span>
                </div>
                <span>{new Date(n.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-3 text-sm text-[#0B1F3A] leading-relaxed">{n.body}</p>
              <div className="mt-2 text-xs text-[#5F6B7A]">— {n.author_name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-[#0B1F3A] text-[#F8F7F2] p-6">
          <div className="eyebrow" style={{ color: "#D4AF37" }}>Automation</div>
          <div className="font-display text-2xl mt-2 leading-tight">Notes flow both ways.</div>
          <p className="mt-4 text-sm text-[#F8F7F2]/70 leading-relaxed">
            Team notes appear on secure counterparty dashboards when marked "shared".
            WhatsApp updates from Lanre are ingested into project timelines automatically.
          </p>
          <div className="hairline mt-6 opacity-30" />
          <ul className="mt-6 space-y-2 text-xs text-[#F8F7F2]/70">
            <li>· Private notes stay with the author</li>
            <li>· Team notes visible to all signed-in staff</li>
            <li>· Shared notes reach the investor portal</li>
          </ul>
        </div>
        <div className="bg-white border border-[#0B1F3A]/10 p-6">
          <div className="eyebrow">Coming Q2</div>
          <div className="font-display text-lg mt-2 text-[#0B1F3A]">Live translation on shared notes</div>
          <p className="mt-2 text-xs text-[#5F6B7A]">Auto-translate WhatsApp updates from Mandarin & Arabic partners before publishing.</p>
        </div>
      </div>
    </div>
  );
}

// ---------------- Project Brief (printable + PDF) ----------------
function ProjectBrief({ project, onClose }) {
  const briefRef = useRef(null);

  if (!project) return null;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return "—"; }
  };

  const downloadPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 54;
      let y = 0;

      // Header band
      doc.setFillColor(11, 31, 58); // navy
      doc.rect(0, 0, W, 74, "F");
      doc.setFillColor(212, 175, 55); // gold hairline
      doc.rect(0, 74, W, 2, "F");
      doc.setTextColor(212, 175, 55);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("FIRST PROVIDENCE CAPITAL", M, 34);
      doc.setTextColor(248, 247, 242);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Confidential Project Brief · Institutional Use", M, 50);
      doc.text(new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }), W - M, 50, { align: "right" });

      y = 110;

      // Project name (serif-styled via bold + large)
      doc.setTextColor(11, 31, 58);
      doc.setFont("times", "bold");
      doc.setFontSize(24);
      const nameLines = doc.splitTextToSize(project.name || "", W - 2 * M);
      doc.text(nameLines, M, y);
      y += nameLines.length * 26 + 4;

      // Sector · Region · Status
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(95, 107, 122);
      doc.text(
        [project.sector, project.region, project.status].filter(Boolean).join("  ·  "),
        M, y
      );
      y += 18;

      // Strategic goal (blue accent line)
      if (project.strategic_goal) {
        doc.setDrawColor(51, 153, 255);
        doc.setLineWidth(2);
        doc.line(M, y + 4, M + 24, y + 4);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(51, 153, 255);
        doc.text("STRATEGIC GOAL", M + 32, y + 8);
        y += 22;
        doc.setFont("times", "italic");
        doc.setFontSize(13);
        doc.setTextColor(11, 31, 58);
        const goalLines = doc.splitTextToSize(project.strategic_goal, W - 2 * M);
        doc.text(goalLines, M, y);
        y += goalLines.length * 16 + 10;
      }

      // Hairline
      doc.setDrawColor(11, 31, 58);
      doc.setLineWidth(0.5);
      doc.line(M, y, W - M, y);
      y += 18;

      // Facts grid (2x4)
      const facts = [
        ["Date Started", fmtDate(project.start_date)],
        ["Date to End", fmtDate(project.target_end_date)],
        ["Size of the Deal", project.deal_size_usd ? fmtUSD(project.deal_size_usd) : "—"],
        ["Capital Deployed", fmtUSD(project.capital_deployed_usd)],
        ["Projected Revenue", fmtUSD(project.projected_revenue_usd)],
        ["Progress", `${project.progress_pct || 0}%`],
        ["Fund Management Fee", project.fund_management_fee_pct != null ? `${project.fund_management_fee_pct}%` : "—"],
        ["Fund Participants Share", project.fund_participants_share_pct != null ? `${project.fund_participants_share_pct}%` : "—"],
      ];
      const colW = (W - 2 * M) / 4;
      facts.forEach((f, i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const x = M + col * colW;
        const yy = y + row * 46;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(95, 107, 122);
        doc.text(f[0].toUpperCase(), x, yy);
        doc.setFont("times", "bold");
        doc.setFontSize(13);
        doc.setTextColor(11, 31, 58);
        const v = doc.splitTextToSize(String(f[1]), colW - 8);
        doc.text(v, x, yy + 16);
      });
      y += 46 * 2 + 12;

      // Brief overview
      const writeSection = (title, body) => {
        if (!body) return;
        if (y > H - 140) { doc.addPage(); y = M; }
        doc.setDrawColor(212, 175, 55);
        doc.setLineWidth(1.5);
        doc.line(M, y, M + 26, y);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(11, 31, 58);
        doc.text(title.toUpperCase(), M + 34, y + 4);
        y += 18;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.setTextColor(45, 55, 72);
        const lines = doc.splitTextToSize(body, W - 2 * M);
        lines.forEach((ln) => {
          if (y > H - 60) { doc.addPage(); y = M; }
          doc.text(ln, M, y);
          y += 14;
        });
        y += 12;
      };

      writeSection("Brief Overview", project.brief_overview);
      writeSection("Financing Structure", project.financing_structure);
      writeSection("Profit Model Framework", project.profit_model);
      writeSection("Cost Analysis", project.cost_analysis);
      writeSection("Pro Forma Financial", project.pro_forma);
      writeSection("Fund Participants", project.fund_participants);

      // Footer on last page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(11, 31, 58);
        doc.rect(0, H - 26, W, 26, "F");
        doc.setTextColor(212, 175, 55);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.text("FIRST PROVIDENCE CAPITAL  ·  CONFIDENTIAL PROJECT BRIEF", M, H - 10);
        doc.setTextColor(248, 247, 242);
        doc.setFont("helvetica", "normal");
        doc.text(`Page ${i} of ${pageCount}`, W - M, H - 10, { align: "right" });
      }

      const safeName = (project.name || "project").replace(/[^a-z0-9]+/gi, "_").slice(0, 60);
      doc.save(`FPC_${safeName}_Brief.pdf`);
      toast.success("Project brief exported");
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    }
  };

  const printBrief = () => {
    // Use a dedicated print window so the brief prints without dashboard chrome
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) { toast.error("Popup blocked — allow popups to print."); return; }
    const html = briefRef.current?.outerHTML || "";
    w.document.write(`<!doctype html><html><head><title>${project.name} — Project Brief</title>
    <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap" rel="stylesheet">
    <style>
      body{font-family:'IBM Plex Sans',sans-serif;background:#F8F7F2;color:#0B1F3A;margin:0;padding:32px}
      .font-display{font-family:'Cormorant Garamond',serif;font-weight:500}
      .eyebrow{text-transform:uppercase;letter-spacing:0.22em;font-size:11px;font-weight:600;color:#3399FF}
      .numeral{font-family:'Cormorant Garamond',serif;font-weight:500;letter-spacing:-0.02em}
      @page { size: letter; margin: 0.4in; }
    </style></head><body>${html}<script>window.onload=function(){window.print();setTimeout(()=>window.close(),400)}<\/script></body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={!!project} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto thin-scroll bg-[#F8F7F2] p-0">
        <DialogHeader className="sr-only"><DialogTitle>Project Brief · {project.name}</DialogTitle></DialogHeader>

        {/* Action bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between bg-[#0B1F3A] text-[#F8F7F2] px-6 py-3 no-print">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">Confidential Project Brief</div>
            <div className="text-sm mt-0.5">{project.name}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={printBrief} variant="outline" className="rounded-none border-[#F8F7F2] text-[#0B1F3A] bg-[#F8F7F2] hover:bg-white gap-2 text-xs uppercase tracking-widest">
              <Printer size={14} /> Print
            </Button>
            <Button onClick={downloadPDF} data-testid="project-brief-download-pdf" className="rounded-none bg-[#D4AF37] text-[#0B1F3A] hover:bg-[#c19e2f] gap-2 text-xs uppercase tracking-widest">
              <Download size={14} /> Download PDF
            </Button>
          </div>
        </div>

        {/* Brief body */}
        <div ref={briefRef} className="p-10 bg-[#F8F7F2]">
          {/* Masthead */}
          <div className="border-b-2 border-[#0B1F3A] pb-4 flex items-center justify-between">
            <div>
              <div className="eyebrow" style={{ color: "#D4AF37" }}>First Providence Capital</div>
              <div className="text-[10px] uppercase tracking-widest text-[#5F6B7A] mt-1">Confidential Project Brief</div>
            </div>
            <div className="text-right text-[10px] uppercase tracking-widest text-[#5F6B7A]">
              {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>

          {/* Title */}
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-[#0B1F3A] mt-8">{project.name}</h1>
          <div className="mt-2 text-sm text-[#5F6B7A]">
            {[project.sector, project.region, project.status].filter(Boolean).join("  ·  ")}
          </div>

          {/* Strategic goal */}
          {project.strategic_goal && (
            <div className="mt-8 border-l-4 border-[#3399FF] pl-5">
              <div className="eyebrow" style={{ color: "#3399FF" }}>Strategic Goal</div>
              <div className="font-display italic text-2xl text-[#0B1F3A] mt-2 leading-snug">{project.strategic_goal}</div>
            </div>
          )}

          {/* Facts grid */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-b border-[#0B1F3A]/15 py-6">
            <BriefFact label="Date Started" value={fmtDate(project.start_date)} />
            <BriefFact label="Date to End" value={fmtDate(project.target_end_date)} />
            <BriefFact label="Size of the Deal" value={project.deal_size_usd ? fmtUSD(project.deal_size_usd) : "—"} />
            <BriefFact label="Capital Deployed" value={fmtUSD(project.capital_deployed_usd)} />
            <BriefFact label="Projected Revenue" value={fmtUSD(project.projected_revenue_usd)} />
            <BriefFact label="Progress" value={`${project.progress_pct || 0}%`} />
            <BriefFact label="Fund Management Fee" value={project.fund_management_fee_pct != null ? `${project.fund_management_fee_pct}%` : "—"} accent />
            <BriefFact label="Fund Participants Share" value={project.fund_participants_share_pct != null ? `${project.fund_participants_share_pct}%` : "—"} accent />
          </div>

          {/* Overview */}
          {project.brief_overview && (
            <BriefSection title="Brief Overview" body={project.brief_overview} />
          )}
          {project.financing_structure && (
            <BriefSection title="Financing Structure" body={project.financing_structure} />
          )}
          {project.profit_model && (
            <BriefSection title="Profit Model Framework" body={project.profit_model} />
          )}
          {project.cost_analysis && (
            <BriefSection title="Cost Analysis" body={project.cost_analysis} />
          )}
          {project.pro_forma && (
            <BriefSection title="Pro Forma Financial" body={project.pro_forma} />
          )}
          {project.fund_participants && (
            <BriefSection title="Fund Participants" body={project.fund_participants} />
          )}

          {/* Footer */}
          <div className="mt-14 pt-6 border-t border-[#0B1F3A]/15 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#5F6B7A]">
            <span>First Providence Capital · Confidential</span>
            <span>Not an offer to sell securities</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BriefFact({ label, value, accent }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.22em] ${accent ? "text-[#D4AF37]" : "text-[#5F6B7A]"} font-semibold`}>{label}</div>
      <div className="numeral text-2xl text-[#0B1F3A] mt-2">{value}</div>
    </div>
  );
}

function BriefSection({ title, body }) {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-3">
        <div className="w-6 h-0.5 bg-[#D4AF37]" />
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#0B1F3A]">{title}</div>
      </div>
      <p className="mt-4 text-[15px] leading-[1.75] text-[#0B1F3A] whitespace-pre-line">{body}</p>
    </div>
  );
}

function fmtUSD(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

